import { FilterParams, HeaderItem, TabType } from "./users.type";
import { RoleType, Roles } from "@/store/auth/auth.type";

import { City } from "@/types";
import { SERVICE_ACCOUNTS_TAB } from "./service-accounts.const";
import { z } from "zod";

const UTF8_BOM = "﻿";

export enum ESortOrder {
  asc = "asc",
  desc = "desc",
  ascending = "ascending",
  descending = "descending",
}

export const Header: TabType[] = [
  Roles.ALL,
  ...Object.values(Roles).filter((r) => r !== Roles.ALL),
  SERVICE_ACCOUNTS_TAB,
];

export const defaultTableColumn: HeaderItem[] = [
  { title: "Name", align: "left" },
  { title: "Role", align: "left" },
  { title: "Last login", align: "left" },
  { title: "Ville", align: "left" },
  { title: "Action", align: "right" },
];

export const clientsHeader: HeaderItem[] = [
  { title: "Name", align: "left" },
  { title: "Email", align: "left" },
  { title: "Phone", align: "left" },
  { title: "Last login", align: "left" },
];

export const initPage: FilterParams = {
  pageIndex: 1,
  pageSize: 10,
};

// Password validation rules
const passwordValidation = (val: string, ctx: z.RefinementCtx) => {
  if (val.length < 8) {
    ctx.addIssue({
      code: "too_small",
      minimum: 8,
      origin: "string",
      inclusive: true,
      message: "Le mot de passe doit contenir au moins 8 caractères",
    });
  }

  if (!/[A-Z]/.test(val)) {
    ctx.addIssue({
      code: "custom",
      message: "Le mot de passe doit contenir au moins une lettre majuscule",
    });
  }

  if (!/[a-z]/.test(val)) {
    ctx.addIssue({
      code: "custom",
      message: "Le mot de passe doit contenir au moins une lettre minuscule",
    });
  }

  if (!/[0-9]/.test(val)) {
    ctx.addIssue({ code: "custom", message: "Le mot de passe doit contenir au moins un chiffre" });
  }

  if (!/[@$!%*?&#]/.test(val)) {
    ctx.addIssue({
      code: "custom",
      message: "Le mot de passe doit contenir au moins un caractère spécial",
    });
  }
};

// Required password schema (for create)
const requiredPasswordSchema = z.string().min(1, "Password is required").superRefine(passwordValidation);

// Optional password schema (for update)
const optionalPasswordSchema = z
  .string()
  .superRefine((val, ctx) => {
    if (!val || val === "") return;
    passwordValidation(val, ctx);
  })
  .optional();

const baseUserFields = {
  firstName: z.string().min(1, "Le prénom doit contenir au moins 1 caractères"),
  lastName: z.string().min(1, "Le nom de famille doit contenir au moins 1 caractères"),
  email: z.email("Adresse e-mail invalide"),
  roles: z.array(z.string()).min(1, "Sélectionnez au moins un rôle"),
  cities: z.array(z.string()).optional(),
  pictureUrl: z.string(),
};

export const createUserSchema = z.object({
  ...baseUserFields,
  password: requiredPasswordSchema,
});

export const updateUserSchema = z.object({
  ...baseUserFields,
  password: optionalPasswordSchema,
});

export const defaultUser = {
  firstName: "",
  lastName: "",
  name: "",
  email: "",
  password: "",
  roles: [Roles.VIEWER],
  cities: [],
  pictureUrl: "",
};

/**
 * Take the management API's CSV export, replace city ids with city names in
 * the `cities` column, and trigger a browser download.
 *
 * The backend export is RFC 4180 with a UTF-8 BOM, CRLF line endings, and a
 * locale-aware field separator (`,` for English, `;` for French/German/...).
 * We detect the delimiter from the first line, locate the cities column by
 * its header (not by position — the layout could evolve), and respect quoted
 * fields when splitting.
 */
export const downloadCsv = (csvData: string, cityData: City[], roleName: RoleType) => {
  if (!csvData || !cityData) return;

  const citiesMap = new Map(cityData.map((city) => [city.id, city.name]));
  const stripped = csvData.startsWith(UTF8_BOM) ? csvData.slice(UTF8_BOM.length) : csvData;
  const lines = stripped.split(/\r?\n/);
  if (lines.length === 0 || !lines[0]) return;

  const delimiter = detectDelimiter(lines[0]);
  const header = parseCsvLine(lines[0], delimiter);
  const citiesIdx = header.findIndex((col) => col.trim().toLowerCase() === "cities");

  const transformed = lines.map((line, index) => {
    if (index === 0 || !line) return line;
    if (citiesIdx < 0) return line;

    const columns = parseCsvLine(line, delimiter);
    const raw = columns[citiesIdx];
    if (!raw) return line;

    const mapped = raw
      .split(/\s*\|\s*/)
      .filter(Boolean)
      .map((cityId) => citiesMap.get(cityId) ?? cityId)
      .join(" | ");
    columns[citiesIdx] = mapped;
    return columns.map((value) => encodeCsvCell(value, delimiter)).join(delimiter);
  });

  const out = UTF8_BOM + transformed.join("\r\n");
  const blobUrl = URL.createObjectURL(new Blob([out], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = `${roleName}_users.csv`;
  a.click();
  URL.revokeObjectURL(blobUrl);
};

/** Detect the field separator by reading the first non-quoted character. */
const detectDelimiter = (headerLine: string): "," | ";" => {
  let inQuotes = false;
  for (let i = 0; i < headerLine.length; i += 1) {
    const ch = headerLine[i];
    if (ch === '"') inQuotes = !inQuotes;
    else if (!inQuotes && (ch === "," || ch === ";")) return ch;
  }
  return ",";
};

/** Parse a single RFC 4180 line: respects double-quoted fields and "" escapes. */
const parseCsvLine = (line: string, delimiter: "," | ";"): string[] => {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      cells.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  cells.push(current);
  return cells;
};

/** Re-encode a cell for CSV output: quote if it contains the delimiter, a quote, or a newline. */
const encodeCsvCell = (value: string, delimiter: "," | ";"): string => {
  if (value === undefined || value === null) return "";
  const needsQuoting = value.includes(delimiter) || value.includes('"') || value.includes("\n") || value.includes("\r");
  if (!needsQuoting) return value;
  return `"${value.replace(/"/g, '""')}"`;
};
